/**
 * MasterBomComparePage.tsx
 *
 * Side-by-side comparison screen for Master BOM and Published BOMs.
 * Route: /purchasing/boms/:productId/compare
 * Spec: docs/superpowers/plans/2026-09-22-bom-master-composer-spec.md
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  GitCompare,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Layers,
  Box,
  SlidersHorizontal,
} from 'lucide-react'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Typeahead } from '@/components/forms/Typeahead'
import {
  PageError,
  PageLoading,
  PageNotFound,
} from '@/components/ui/PageAsyncState'
import { productService, type Product } from '@/services/products'
import {
  getMasterCombosApi,
  compareMasterBomApi,
} from '@/services/boms/masterApiClient'
import type {
  MasterComboRow,
  MasterCompareResult,
  CompareSource,
  WoodDiffItem,
  HardwareDiffItem,
} from '@/services/boms/masterTypes'

type ComboOption = {
  key: string
  label: string
  combo: MasterComboRow
}

interface MasterBomComparePageProps {
  initialProduct?: Product | null
  initialCombos?: MasterComboRow[]
  initialCompareResult?: MasterCompareResult | null
}

export function MasterBomComparePage({
  initialProduct = null,
  initialCombos = [],
  initialCompareResult = null,
}: MasterBomComparePageProps = {}) {
  const { productId } = useParams<{ productId: string }>()
  const navigate = useNavigate()

  const [product, setProduct] = useState<Product | null>(initialProduct)
  const [combos, setCombos] = useState<MasterComboRow[]>(initialCombos)
  const [loading, setLoading] = useState(!initialProduct)
  const [comparing, setComparing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Selector state
  const [sourceA, setSourceA] = useState<CompareSource>('master')
  const [comboKeyA, setComboKeyA] = useState<string>(
    initialCombos.length > 0
      ? `${initialCombos[0]?.variant_id}_${initialCombos[0]?.assembly_type}`
      : '',
  )
  const [sourceB, setSourceB] = useState<CompareSource>('published')
  const [comboKeyB, setComboKeyB] = useState<string>(
    initialCombos.length > 0
      ? `${initialCombos[0]?.variant_id}_${initialCombos[0]?.assembly_type}`
      : '',
  )

  // Diff result
  const [compareResult, setCompareResult] = useState<MasterCompareResult | null>(
    initialCompareResult,
  )

  // Filter: show only differing rows
  const [onlyDiffs, setOnlyDiffs] = useState(false)

  const buildComboOptions = useCallback(
    (source: CompareSource): ComboOption[] =>
      combos
        .filter((c) => source === 'master' || Boolean(c.published_bom))
        .map((c) => {
          const key = `${c.variant_id}_${c.assembly_type}`
          const hasPublished = Boolean(c.published_bom)
          const label =
            `${c.combo_code} · ${c.color_name} (${c.assembly_label})` +
            (source === 'published' && hasPublished
              ? ` — v${c.published_bom?.version_seq}`
              : '')
          return { key, label, combo: c }
        }),
    [combos],
  )

  const comboOptionsA = useMemo(
    () => buildComboOptions(sourceA),
    [buildComboOptions, sourceA],
  )
  const comboOptionsB = useMemo(
    () => buildComboOptions(sourceB),
    [buildComboOptions, sourceB],
  )

  // Initial load: product and combos
  const loadInitialData = useCallback(async () => {
    if (!productId) return
    setLoading(true)
    setError(null)
    try {
      const [prod, comboList] = await Promise.all([
        productService.getById(productId),
        getMasterCombosApi(productId).catch(() => [] as MasterComboRow[]),
      ])

      if (!prod) {
        setProduct(null)
        setError('Sản phẩm không tồn tại')
        return
      }

      setProduct(prod)
      setCombos(comboList)

      if (comboList.length > 0) {
        const firstKey = `${comboList[0]?.variant_id}_${comboList[0]?.assembly_type}`
        setComboKeyA(firstKey)

        // Find first combo with published BOM for Side B if possible
        const pubCombo = comboList.find((c) => Boolean(c.published_bom))
        if (pubCombo) {
          setComboKeyB(`${pubCombo.variant_id}_${pubCombo.assembly_type}`)
          setSourceB('published')
        } else {
          setComboKeyB(firstKey)
          setSourceB('master')
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Tải dữ liệu thất bại')
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => {
    if (!initialProduct) {
      void loadInitialData()
    }
  }, [loadInitialData, initialProduct])

  // Run compare when selectors change
  const runCompare = useCallback(async () => {
    if (!productId || !comboKeyA || !comboKeyB) return
    const comboA = combos.find(
      (c) => `${c.variant_id}_${c.assembly_type}` === comboKeyA,
    )
    const comboB = combos.find(
      (c) => `${c.variant_id}_${c.assembly_type}` === comboKeyB,
    )
    if (!comboA || !comboB) return

    setComparing(true)
    setError(null)
    try {
      const params: Parameters<typeof compareMasterBomApi>[1] = {
        source_a: sourceA,
        variant_a: comboA.variant_id,
        asm_a: comboA.assembly_type,
        source_b: sourceB,
        variant_b: comboB.variant_id,
        asm_b: comboB.assembly_type,
      }
      if (sourceA === 'published' && comboA.published_bom) {
        params.bom_a = comboA.published_bom.id
      }
      if (sourceB === 'published' && comboB.published_bom) {
        params.bom_b = comboB.published_bom.id
      }

      const result = await compareMasterBomApi(productId, params)
      setCompareResult(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'So sánh BOM thất bại')
    } finally {
      setComparing(false)
    }
  }, [productId, comboKeyA, sourceA, comboKeyB, sourceB, combos])

  useEffect(() => {
    if (comboKeyA && comboKeyB && !initialCompareResult) {
      void runCompare()
    }
  }, [runCompare, comboKeyA, comboKeyB, sourceA, sourceB, initialCompareResult])

  if (loading) {
    return <PageLoading label="Đang tải dữ liệu so sánh BOM..." />
  }

  if (error && !product) {
    return (
      <div className="p-6 space-y-4">
        <PageError message={error} onRetry={() => void loadInitialData()} />
        <Button variant="secondary" onClick={() => navigate('/purchasing/boms')}>
          Về danh sách BOM
        </Button>
      </div>
    )
  }

  if (!product) {
    return (
      <PageNotFound
        title="Không tìm thấy sản phẩm"
        description="Sản phẩm không tồn tại hoặc đã bị xóa."
        action={
          <Button variant="secondary" onClick={() => navigate('/purchasing/boms')}>
            Về danh sách BOM
          </Button>
        }
      />
    )
  }

  // Filter diff items by status
  const filterWoodItems = (items: WoodDiffItem[]) => {
    if (!onlyDiffs) return items
    return items.filter((item) => item.status !== 'identical')
  }

  const filterHardwareItems = (items: HardwareDiffItem[]) => {
    if (!onlyDiffs) return items
    return items.filter((item) => item.status !== 'identical')
  }

  const filteredWood = compareResult ? filterWoodItems(compareResult.wood_diff) : []
  const filteredHardware = compareResult
    ? filterHardwareItems(compareResult.hardware_diff)
    : []

  return (
    <div className="space-y-4 pb-12">
      {/* 1. Breadcrumbs */}
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Định mức (BOM)', to: '/purchasing/boms' },
          { label: product.code, to: `/products/${product.id}` },
          {
            label: 'Master BOM',
            to: `/purchasing/boms/${product.id}/master`,
          },
          { label: 'So sánh khác biệt (Diff)' },
        ]}
      />

      {/* 2. Top Header */}
      <div className="bg-white p-5 rounded-lg border border-slate-300 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Badge tone="neutral" className="uppercase tracking-wider">
              <GitCompare className="h-3.5 w-3.5" />
              So sánh BOM
            </Badge>
            <h1 className="text-xl font-black text-slate-900">{product.code}</h1>
            <span className="text-sm text-slate-500 font-medium">({product.name})</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            So sánh chi tiết từng dòng gỗ và ngũ kim giữa Master BOM hoặc các phiên bản đã Publish
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate(`/purchasing/boms/${product.id}/master`)}
            className="flex items-center gap-1.5 text-xs"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Về Master BOM</span>
          </Button>
        </div>
      </div>

      {/* 3. Selector Panel (Side A vs Side B) */}
      <div className="bg-white p-5 rounded-lg border border-slate-300 grid grid-cols-1 md:grid-cols-2 gap-6 relative">
        {/* Side A Selector */}
        <div className="space-y-3 p-4 rounded-lg bg-slate-50 border border-slate-300">
          <div className="flex items-center justify-between">
            <Badge tone="info">Bên A (Nguồn so sánh)</Badge>
            <div className="flex items-center gap-1 text-xs">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="source_a"
                  value="master"
                  checked={sourceA === 'master'}
                  onChange={() => setSourceA('master')}
                  className="text-slate-700 focus:ring-slate-400"
                />
                <span className="text-slate-700 font-medium">Master BOM</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer ml-2">
                <input
                  type="radio"
                  name="source_a"
                  value="published"
                  checked={sourceA === 'published'}
                  onChange={() => setSourceA('published')}
                  className="text-slate-700 focus:ring-slate-400"
                />
                <span className="text-slate-700 font-medium">Đã Publish</span>
              </label>
            </div>
          </div>

          <div>
            <label
              htmlFor="side-a-combo-select"
              className="block text-xs font-semibold text-slate-700 mb-1"
            >
              Chọn cấu hình (Màu × Kết cấu):
            </label>
            <Typeahead<ComboOption>
              id="side-a-combo-select"
              items={comboOptionsA}
              value={comboOptionsA.find((o) => o.key === comboKeyA) || null}
              onChange={(item) => {
                if (item) setComboKeyA(item.key)
              }}
              displayValue={(o) => o.label}
              getItemKey={(o) => o.key}
              placeholder="Tìm cấu hình bên A..."
              clearable={false}
              inputClassName="h-9 text-xs bg-white"
              aria-label="Chọn cấu hình bên A"
            />
          </div>
        </div>

        {/* Side B Selector */}
        <div className="space-y-3 p-4 rounded-lg bg-slate-50 border border-slate-300">
          <div className="flex items-center justify-between">
            <Badge tone="neutral">Bên B (Đích đối chiếu)</Badge>
            <div className="flex items-center gap-1 text-xs">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="radio"
                  name="source_b"
                  value="published"
                  checked={sourceB === 'published'}
                  onChange={() => setSourceB('published')}
                  className="text-slate-700 focus:ring-slate-400"
                />
                <span className="text-slate-700 font-medium">Đã Publish</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer ml-2">
                <input
                  type="radio"
                  name="source_b"
                  value="master"
                  checked={sourceB === 'master'}
                  onChange={() => setSourceB('master')}
                  className="text-slate-700 focus:ring-slate-400"
                />
                <span className="text-slate-700 font-medium">Master BOM</span>
              </label>
            </div>
          </div>

          <div>
            <label
              htmlFor="side-b-combo-select"
              className="block text-xs font-semibold text-slate-700 mb-1"
            >
              Chọn cấu hình (Màu × Kết cấu):
            </label>
            <Typeahead<ComboOption>
              id="side-b-combo-select"
              items={comboOptionsB}
              value={comboOptionsB.find((o) => o.key === comboKeyB) || null}
              onChange={(item) => {
                if (item) setComboKeyB(item.key)
              }}
              displayValue={(o) => o.label}
              getItemKey={(o) => o.key}
              placeholder="Tìm cấu hình bên B..."
              clearable={false}
              inputClassName="h-9 text-xs bg-white"
              aria-label="Chọn cấu hình bên B"
            />
          </div>
        </div>
      </div>

      {/* 4. Error state if any */}
      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-xs text-rose-800 flex items-center gap-3">
          <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* 5. Summary KPI Bar */}
      {compareResult && (
        <div className="bg-white p-4 rounded-lg border border-slate-300 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-300 pb-3">
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <SlidersHorizontal className="h-3.5 w-3.5 text-slate-500" />
              <span className="font-semibold text-slate-900">Tóm tắt kết quả so sánh:</span>
              <span className="text-slate-500 font-mono">
                {compareResult.side_a.label} vs {compareResult.side_b.label}
              </span>
            </div>

            <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={onlyDiffs}
                onChange={(e) => setOnlyDiffs(e.target.checked)}
                className="rounded border-slate-300 text-slate-700 focus:ring-slate-400"
              />
              <span>Chỉ hiện dòng có khác biệt</span>
            </label>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            {/* Identical */}
            <div className="p-2.5 rounded-lg bg-white border border-slate-300">
              <div className="text-xs font-medium text-slate-700 flex items-center justify-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                <span>Giống nhau</span>
              </div>
              <div className="text-lg font-black text-slate-900 mt-0.5">
                {compareResult.summary.total.identical}
              </div>
              <Badge tone="success" className="mt-1">
                Gỗ: {compareResult.summary.wood.identical} · NK:{' '}
                {compareResult.summary.hardware.identical}
              </Badge>
            </div>

            {/* Changed — KEEP amber warning */}
            <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200">
              <div className="text-xs font-medium text-amber-700 flex items-center justify-center gap-1">
                <AlertTriangle className="h-3 w-3 text-amber-600" />
                <span>Khác thông số</span>
              </div>
              <div className="text-lg font-black text-amber-800 mt-0.5">
                {compareResult.summary.total.changed}
              </div>
              <div className="text-xs text-amber-600/80">
                Gỗ: {compareResult.summary.wood.changed} · NK:{' '}
                {compareResult.summary.hardware.changed}
              </div>
            </div>

            {/* Only in A */}
            <div className="p-2.5 rounded-lg bg-white border border-slate-300">
              <div className="text-xs font-medium text-slate-700">Chỉ có ở A</div>
              <div className="text-lg font-black text-slate-900 mt-0.5">
                {compareResult.summary.total.only_in_a}
              </div>
              <Badge tone="info" className="mt-1">
                Gỗ: {compareResult.summary.wood.only_in_a} · NK:{' '}
                {compareResult.summary.hardware.only_in_a}
              </Badge>
            </div>

            {/* Only in B */}
            <div className="p-2.5 rounded-lg bg-white border border-slate-300">
              <div className="text-xs font-medium text-slate-700">Chỉ có ở B</div>
              <div className="text-lg font-black text-slate-900 mt-0.5">
                {compareResult.summary.total.only_in_b}
              </div>
              <Badge tone="neutral" className="mt-1">
                Gỗ: {compareResult.summary.wood.only_in_b} · NK:{' '}
                {compareResult.summary.hardware.only_in_b}
              </Badge>
            </div>
          </div>
        </div>
      )}

      {comparing && (
        <div className="py-8 text-center text-slate-500 text-xs">
          Đang tính toán so sánh giữa hai cấu hình...
        </div>
      )}

      {/* 6. Wood Comparison Table */}
      {compareResult && (
        <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-300 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-slate-600" />
              <h3 className="text-sm font-bold text-slate-900">
                Chi tiết Gỗ (Wood Lines Diff)
              </h3>
              <Badge tone="neutral">{filteredWood.length} chi tiết</Badge>
            </div>
            <span className="text-xs text-slate-500 italic">
              * Ô tô vàng biểu thị giá trị khác nhau giữa hai bên
            </span>
          </div>

          {filteredWood.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-xs">
              {onlyDiffs
                ? 'Không có sự khác biệt nào ở bảng chi tiết gỗ.'
                : 'Không có dòng gỗ nào để so sánh.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left border-collapse text-slate-900">
                <thead>
                  <tr className="bg-slate-50 font-semibold text-slate-900">
                    <th className="py-2.5 px-3 w-10 text-center border border-slate-300">STT</th>
                    <th className="py-2.5 px-3 w-28 text-center border border-slate-300">Trạng thái</th>
                    <th className="py-2.5 px-3 w-48 border border-slate-300">Tên chi tiết</th>
                    <th className="py-2.5 px-2 w-16 text-center border border-slate-300">Bên</th>
                    <th className="py-2.5 px-3 w-28 border border-slate-300">Loại gỗ</th>
                    <th className="py-2.5 px-3 w-28 text-center border border-slate-300">KT Thành phẩm</th>
                    <th className="py-2.5 px-3 w-28 text-center border border-slate-300">KT Thô</th>
                    <th className="py-2.5 px-3 w-20 text-center border border-slate-300">Mộng</th>
                    <th className="py-2.5 px-3 w-16 text-right border border-slate-300">SL/SP</th>
                    <th className="py-2.5 px-3 w-24 border border-slate-300">Veneer</th>
                    <th className="py-2.5 px-3 w-32 border border-slate-300">Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWood.map((item, index) => {
                    const isDiff = item.status === 'changed'
                    const isOnlyA = item.status === 'only_in_a'
                    const isOnlyB = item.status === 'only_in_b'
                    const isIdentical = item.status === 'identical'

                    const diffFields = item.diff_fields

                    return (
                      <tr
                        key={`${item.part_name}-${index}`}
                        className={`transition-colors ${
                          isDiff
                            ? 'bg-amber-50/20'
                            : isOnlyA
                            ? 'bg-sky-50/30'
                            : isOnlyB
                            ? 'bg-slate-50'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="py-3 px-3 text-center text-slate-500 font-mono align-top border border-slate-300 bg-white">
                          {index + 1}
                        </td>

                        {/* Status Badge */}
                        <td className="py-3 px-3 text-center align-top border border-slate-300 bg-white">
                          {isIdentical && <Badge tone="success">Giống nhau</Badge>}
                          {isDiff && <Badge tone="warning">Khác thông số</Badge>}
                          {isOnlyA && <Badge tone="info">Chỉ có ở A</Badge>}
                          {isOnlyB && <Badge tone="neutral">Chỉ có ở B</Badge>}
                        </td>

                        {/* Part name */}
                        <td className="py-3 px-3 font-semibold text-slate-900 align-top border border-slate-300 bg-white">
                          {item.part_name}
                        </td>

                        {/* 2-line comparison (Side A / Side B) */}
                        <td colSpan={8} className="p-0 border border-slate-300">
                          <div className="divide-y divide-slate-300">
                            {/* Side A Row */}
                            <div className="flex items-center py-1.5 px-2 bg-white">
                              <span className="w-14 shrink-0 font-bold text-slate-700 font-mono text-xs">
                                [A]
                              </span>
                              {item.side_a ? (
                                <div className="grid grid-cols-7 gap-2 flex-1 items-center">
                                  <span
                                    className={`truncate ${
                                      diffFields.includes('wood_type_raw')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_a.wood_type_raw || '—'}
                                  </span>

                                  <span
                                    className={`text-center font-mono ${
                                      diffFields.includes('dims_finished')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_a.dims_finished_t ?? '—'} ×{' '}
                                    {item.side_a.dims_finished_w ?? '—'} ×{' '}
                                    {item.side_a.dims_finished_l ?? '—'}
                                  </span>

                                  <span
                                    className={`text-center font-mono ${
                                      diffFields.includes('dims_rough')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-500'
                                    }`}
                                  >
                                    {item.side_a.dims_rough_t ?? '—'} ×{' '}
                                    {item.side_a.dims_rough_w ?? '—'} ×{' '}
                                    {item.side_a.dims_rough_l ?? '—'}
                                  </span>

                                  <span
                                    className={`text-center ${
                                      diffFields.includes('tenon')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-600'
                                    }`}
                                  >
                                    {item.side_a.tenon || '—'}
                                  </span>

                                  <span
                                    className={`text-right font-mono font-bold ${
                                      diffFields.includes('qty_per')
                                        ? 'bg-amber-200/70 text-amber-900 px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_a.qty_per}
                                  </span>

                                  <span
                                    className={`truncate ${
                                      diffFields.includes('veneer_spec')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-600'
                                    }`}
                                  >
                                    {item.side_a.veneer_spec || '—'}
                                  </span>

                                  <span
                                    className={`truncate italic ${
                                      diffFields.includes('notes')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-500'
                                    }`}
                                  >
                                    {item.side_a.notes || '—'}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-400 italic text-xs">
                                  — Không có ở Bên A —
                                </span>
                              )}
                            </div>

                            {/* Side B Row */}
                            <div className="flex items-center py-1.5 px-2 bg-slate-50/50">
                              <span className="w-14 shrink-0 font-bold text-slate-700 font-mono text-xs">
                                [B]
                              </span>
                              {item.side_b ? (
                                <div className="grid grid-cols-7 gap-2 flex-1 items-center">
                                  <span
                                    className={`truncate ${
                                      diffFields.includes('wood_type_raw')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_b.wood_type_raw || '—'}
                                  </span>

                                  <span
                                    className={`text-center font-mono ${
                                      diffFields.includes('dims_finished')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_b.dims_finished_t ?? '—'} ×{' '}
                                    {item.side_b.dims_finished_w ?? '—'} ×{' '}
                                    {item.side_b.dims_finished_l ?? '—'}
                                  </span>

                                  <span
                                    className={`text-center font-mono ${
                                      diffFields.includes('dims_rough')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-500'
                                    }`}
                                  >
                                    {item.side_b.dims_rough_t ?? '—'} ×{' '}
                                    {item.side_b.dims_rough_w ?? '—'} ×{' '}
                                    {item.side_b.dims_rough_l ?? '—'}
                                  </span>

                                  <span
                                    className={`text-center ${
                                      diffFields.includes('tenon')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-600'
                                    }`}
                                  >
                                    {item.side_b.tenon || '—'}
                                  </span>

                                  <span
                                    className={`text-right font-mono font-bold ${
                                      diffFields.includes('qty_per')
                                        ? 'bg-amber-200/70 text-amber-900 px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_b.qty_per}
                                  </span>

                                  <span
                                    className={`truncate ${
                                      diffFields.includes('veneer_spec')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-600'
                                    }`}
                                  >
                                    {item.side_b.veneer_spec || '—'}
                                  </span>

                                  <span
                                    className={`truncate italic ${
                                      diffFields.includes('notes')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-500'
                                    }`}
                                  >
                                    {item.side_b.notes || '—'}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-400 italic text-xs">
                                  — Không có ở Bên B —
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* 7. Hardware Comparison Table */}
      {compareResult && (
        <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-300 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2">
              <Box className="h-4 w-4 text-slate-600" />
              <h3 className="text-sm font-bold text-slate-900">
                Ngũ kim & Phụ kiện (Hardware Lines Diff)
              </h3>
              <Badge tone="neutral">{filteredHardware.length} vật tư</Badge>
            </div>
            <span className="text-xs text-slate-500 italic">
              * Ô tô vàng biểu thị giá trị khác nhau giữa hai bên
            </span>
          </div>

          {filteredHardware.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-xs">
              {onlyDiffs
                ? 'Không có sự khác biệt nào ở bảng ngũ kim.'
                : 'Không có dòng ngũ kim nào để so sánh.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left border-collapse text-slate-900">
                <thead>
                  <tr className="bg-slate-50 font-semibold text-slate-900">
                    <th className="py-2.5 px-3 w-10 text-center border border-slate-300">STT</th>
                    <th className="py-2.5 px-3 w-28 text-center border border-slate-300">Trạng thái</th>
                    <th className="py-2.5 px-3 w-56 border border-slate-300">Tên vật tư</th>
                    <th className="py-2.5 px-2 w-16 text-center border border-slate-300">Bên</th>
                    <th className="py-2.5 px-3 w-44 border border-slate-300">Quy cách / Thông số</th>
                    <th className="py-2.5 px-3 w-20 text-center border border-slate-300">ĐVT</th>
                    <th className="py-2.5 px-3 w-20 text-right border border-slate-300">SL/SP</th>
                    <th className="py-2.5 px-3 w-28 border border-slate-300">Gợi ý màu</th>
                    <th className="py-2.5 px-3 w-36 border border-slate-300">Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHardware.map((item, index) => {
                    const isDiff = item.status === 'changed'
                    const isOnlyA = item.status === 'only_in_a'
                    const isOnlyB = item.status === 'only_in_b'
                    const isIdentical = item.status === 'identical'

                    const diffFields = item.diff_fields

                    return (
                      <tr
                        key={`${item.name_raw}-${index}`}
                        className={`transition-colors ${
                          isDiff
                            ? 'bg-amber-50/20'
                            : isOnlyA
                            ? 'bg-sky-50/30'
                            : isOnlyB
                            ? 'bg-slate-50'
                            : 'hover:bg-slate-50'
                        }`}
                      >
                        <td className="py-3 px-3 text-center text-slate-500 font-mono align-top border border-slate-300 bg-white">
                          {index + 1}
                        </td>

                        {/* Status Badge */}
                        <td className="py-3 px-3 text-center align-top border border-slate-300 bg-white">
                          {isIdentical && <Badge tone="success">Giống nhau</Badge>}
                          {isDiff && <Badge tone="warning">Khác thông số</Badge>}
                          {isOnlyA && <Badge tone="info">Chỉ có ở A</Badge>}
                          {isOnlyB && <Badge tone="neutral">Chỉ có ở B</Badge>}
                        </td>

                        {/* Hardware name */}
                        <td className="py-3 px-3 font-semibold text-slate-900 align-top border border-slate-300 bg-white">
                          {item.name_raw}
                        </td>

                        {/* 2-line comparison (Side A / Side B) */}
                        <td colSpan={6} className="p-0 border border-slate-300">
                          <div className="divide-y divide-slate-300">
                            {/* Side A Row */}
                            <div className="flex items-center py-1.5 px-2 bg-white">
                              <span className="w-14 shrink-0 font-bold text-slate-700 font-mono text-xs">
                                [A]
                              </span>
                              {item.side_a ? (
                                <div className="grid grid-cols-5 gap-2 flex-1 items-center">
                                  <span
                                    className={`truncate ${
                                      diffFields.includes('spec_raw')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_a.spec_raw || '—'}
                                  </span>

                                  <span
                                    className={`text-center font-medium ${
                                      diffFields.includes('uom_hint')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-600'
                                    }`}
                                  >
                                    {item.side_a.uom_hint || '—'}
                                  </span>

                                  <span
                                    className={`text-right font-mono font-bold ${
                                      diffFields.includes('qty_per')
                                        ? 'bg-amber-200/70 text-amber-900 px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_a.qty_per}
                                  </span>

                                  <span
                                    className={`truncate ${
                                      diffFields.includes('color_hint')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-600'
                                    }`}
                                  >
                                    {item.side_a.color_hint || '—'}
                                  </span>

                                  <span
                                    className={`truncate italic ${
                                      diffFields.includes('notes')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-500'
                                    }`}
                                  >
                                    {item.side_a.notes || '—'}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-400 italic text-xs">
                                  — Không có ở Bên A —
                                </span>
                              )}
                            </div>

                            {/* Side B Row */}
                            <div className="flex items-center py-1.5 px-2 bg-slate-50/50">
                              <span className="w-14 shrink-0 font-bold text-slate-700 font-mono text-xs">
                                [B]
                              </span>
                              {item.side_b ? (
                                <div className="grid grid-cols-5 gap-2 flex-1 items-center">
                                  <span
                                    className={`truncate ${
                                      diffFields.includes('spec_raw')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_b.spec_raw || '—'}
                                  </span>

                                  <span
                                    className={`text-center font-medium ${
                                      diffFields.includes('uom_hint')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-600'
                                    }`}
                                  >
                                    {item.side_b.uom_hint || '—'}
                                  </span>

                                  <span
                                    className={`text-right font-mono font-bold ${
                                      diffFields.includes('qty_per')
                                        ? 'bg-amber-200/70 text-amber-900 px-1 rounded'
                                        : 'text-slate-900'
                                    }`}
                                  >
                                    {item.side_b.qty_per}
                                  </span>

                                  <span
                                    className={`truncate ${
                                      diffFields.includes('color_hint')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-600'
                                    }`}
                                  >
                                    {item.side_b.color_hint || '—'}
                                  </span>

                                  <span
                                    className={`truncate italic ${
                                      diffFields.includes('notes')
                                        ? 'bg-amber-200/70 text-amber-900 font-bold px-1 rounded'
                                        : 'text-slate-500'
                                    }`}
                                  >
                                    {item.side_b.notes || '—'}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-400 italic text-xs">
                                  — Không có ở Bên B —
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
